// Benchmark's queries: who the user is to it, the case lists per tab (of the account selected in the app), and
// editors' progress (of that account too).

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { currentAccountIdAtom } from '@/hooks/use-user-accounts';
import { goldenService } from '@/services/golden.service';
import type { GoldenTab } from '@/types/golden.types';

export const GOLDEN_QUERY_KEY = ['sentinel', 'golden'] as const;

export const goldenMeQueryAtom = atomWithQuery(() => ({
  queryKey: [...GOLDEN_QUERY_KEY, 'me'],
  queryFn: goldenService.me,
  staleTime: 5 * 60_000,
  retry: false,
}));

const hasAccessAtom = atom((get) => {
  const role = get(goldenMeQueryAtom).data?.role;
  return role === 'sentinel' || role === 'editor';
});

/** Who gets Benchmark: editors, and Sentinels of the selected account. `null` until the check answers; `false` if it fails. */
export const hasBenchmarkAtom = atom((get): boolean | null => {
  const check = get(goldenMeQueryAtom);
  if (check.isPending) return null;
  const me = check.data;
  const account = get(currentAccountIdAtom);
  return me?.role === 'editor' || (me?.role === 'sentinel' && account !== null && me.accounts.includes(account));
});

// The account is in the key, so switching account refetches.
const casesQuery = (tab: GoldenTab, account: number | null, hasAccess: boolean) => ({
  queryKey: [...GOLDEN_QUERY_KEY, 'cases', tab, account],
  queryFn: () => goldenService.listCases(tab, account as number),
  enabled: hasAccess && account !== null,
  retry: false,
});

/** To do lists these first, each with Continue. */
export const benchmarkInProgressQueryAtom = atomWithQuery((get) => casesQuery('in_progress', get(currentAccountIdAtom), get(hasAccessAtom)));

export const benchmarkDoneQueryAtom = atomWithQuery((get) => casesQuery('done', get(currentAccountIdAtom), get(hasAccessAtom)));

/** To do in its fixed per-Sentinel order (for editors: every case): the counts, Start next, Next case and "Case n of N to do". */
export const benchmarkTodoQueryAtom = atomWithQuery((get) => casesQuery('todo', get(currentAccountIdAtom), get(hasAccessAtom)));

export const benchmarkProgressQueryAtom = atomWithQuery((get) => {
  const account = get(currentAccountIdAtom);
  return {
    queryKey: [...GOLDEN_QUERY_KEY, 'progress', account],
    queryFn: () => goldenService.progress(account as number),
    enabled: get(goldenMeQueryAtom).data?.role === 'editor' && account !== null,
    retry: false,
  };
});
